import React from 'react'
import PropTypes from 'prop-types'
import { createUseStyles } from 'react-jss'
import { kebabCase } from 'lodash'

const useStyles = createUseStyles(({ colors, typography }) => ({
  wrapper: {
    display: 'flex',
    cursor: 'pointer',
    margin: '0 0 12px 0'
  },

  radio: {
    position: 'absolute',
    opacity: 0,

    '&:checked + label:before': {
      background: ({ color }) => colors[color],
      boxShadow: `inset 0 0 0 2px ${colors.light}`
    },

    '&:disabled + label:before': {
      background: colors.greyLight,
      borderColor: colors.greyLight,
      boxShadow: `inset 0 0 0 2px ${colors.light}`,
      cursor: 'not-allowed'
    },

    '&:disabled + label': {
      color: colors.greyLight,
      cursor: 'not-allowed'
    }
  },

  label: {
    display: 'flex',
    alignItems: 'flex-start',
    lineHeight: 1.2,
    userSelect: 'none',
    color: ({ textColor }) => colors[textColor] || colors.dark,
    fontFamily: typography.fontFamilyVariants.primary,
    fontSize: ({ textSize }) =>
      typography.fontSizeVariants[textSize] ||
      typography.fontSizeVariants.medium,
    fontWeight: ({ textWeight }) =>
      typography.fontWeightVariants[textWeight] ||
      typography.fontWeightVariants.regular,
    position: 'relative',

    '&:before': {
      content: "''",
      background: 'transparent',
      borderRadius: '100%',
      border: ({ color }) => `1px solid ${colors[color]}`,
      width: typography.fontSizeVariants.medium,
      height: typography.fontSizeVariants.medium,
      marginRight: 8,
      marginTop: 2,
      flexShrink: 0,
      transition: 'all 250ms ease'
    }
  },

  labelContent: {
    display: 'flex',
    flexDirection: 'column'
  },

  labelText: {
    display: 'block',
    marginBottom: 2
  },

  subLabel: {
    marginTop: 2,
    color: ({ subLabelColor }) => colors[subLabelColor] || colors.greyDark,
    fontSize: ({ subLabelSize }) =>
      typography.fontSizeVariants[subLabelSize] ||
      typography.fontSizeVariants.xSmall,
    fontWeight: ({ subLabelWeight }) =>
      typography.fontWeightVariants[subLabelWeight] ||
      typography.fontWeightVariants.regular
  }
}))

function RadioInput({
  defaultChecked,
  label,
  name,
  onChange,
  input,
  value,
  color,
  textColor,
  textSize,
  textWeight,
  subLabel,
  subLabelColor,
  subLabelSize,
  subLabelWeight,
  ...other
}) {
  const classes = useStyles({
    color,
    textColor,
    textSize,
    textWeight,
    subLabelColor,
    subLabelSize,
    subLabelWeight
  })

  const handleChange = e => {
    if (onChange) {
      onChange(e)
    }
    if (input?.onChange) {
      input.onChange(e)
    }
  }

  return (
    <div className={classes.wrapper}>
      <input
        className={classes.radio}
        name={name}
        onChange={handleChange}
        id={kebabCase(label)}
        type="radio"
        defaultChecked={defaultChecked}
        value={value}
        {...input}
        {...other}
      />
      <label className={classes.label} htmlFor={kebabCase(label)}>
        <span className={classes.labelContent}>
          <span className={classes.labelText}>{label}</span>
          {subLabel && <span className={classes.subLabel}>{subLabel}</span>}
        </span>
      </label>
    </div>
  )
}

RadioInput.propTypes = {
  label: PropTypes.string.isRequired,
  defaultChecked: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,

  textColor: PropTypes.string,
  textSize: PropTypes.oneOf([
    'xxxLarge',
    'xxLarge',
    'xLarge',
    'large',
    'medium',
    'small',
    'xSmall',
    'xxSmall',
    'xxxSmall'
  ]),
  textWeight: PropTypes.oneOf([
    'extralight',
    'light',
    'regular',
    'medium',
    'semibold',
    'bold'
  ]),

  subLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subLabelColor: PropTypes.string,
  subLabelSize: PropTypes.oneOf([
    'xxxLarge',
    'xxLarge',
    'xLarge',
    'large',
    'medium',
    'small',
    'xSmall',
    'xxSmall',
    'xxxSmall'
  ]),
  subLabelWeight: PropTypes.oneOf([
    'extralight',
    'light',
    'regular',
    'medium',
    'semibold',
    'bold'
  ])
}

RadioInput.defaultProps = {
  defaultChecked: false,
  onChange: () => null,
  value: '',
  color: 'tertiary',
  textColor: 'dark',
  textSize: 'medium',
  textWeight: 'medium',
  subLabel: null,
  subLabelColor: 'greyDark',
  subLabelSize: 'xSmall',
  subLabelWeight: 'regular'
}

export default RadioInput
